import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { useCare } from '../contexts/CareContext'
import { useToast, humanizeError } from '../components/shared/Toast'
import LoadingSpinner from '../components/shared/LoadingSpinner'
import EmptyState from '../components/shared/EmptyState'
import TaskForm from '../components/tasks/TaskForm'

const todayISO = () => new Date().toLocaleDateString('en-CA')
const fmtDate = (iso) =>
  new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
const initialOf = (n) => (n || '?').trim().charAt(0).toUpperCase()

export default function TasksPage() {
  const { user } = useAuth()
  const { recipient, role } = useCare()
  const toast = useToast()
  const canManage = role === 'admin' || role === 'family_member'

  const [mineOnly, setMineOnly] = useState(false)
  const [tasks, setTasks] = useState([])
  const [team, setTeam] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(null)
  const [showDone, setShowDone] = useState(false)
  const [assigning, setAssigning] = useState(null) // task id with the picker open
  const today = todayISO()

  const load = useCallback(async () => {
    if (!recipient) return
    const [taskRes, teamRes] = await Promise.all([
      supabase
        .from('tasks')
        .select('*, assignee:profiles!assigned_to(full_name)')
        .eq('care_recipient_id', recipient.id)
        .order('is_completed', { ascending: true })
        .order('due_date', { ascending: true, nullsFirst: false }),
      supabase
        .from('care_team_members')
        .select('user_id, profile:profiles(full_name)')
        .eq('care_recipient_id', recipient.id)
        .eq('is_active', true)
        .not('user_id', 'is', null),
    ])
    setTasks(taskRes.data || [])
    setTeam(
      (teamRes.data || []).map((m) => ({
        user_id: m.user_id,
        name: m.profile?.full_name || 'Member',
      }))
    )
    setLoading(false)
  }, [recipient])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!recipient) return
    const ch = supabase
      .channel(`tasks-${recipient.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tasks', filter: `care_recipient_id=eq.${recipient.id}` },
        () => load()
      )
      .subscribe()
    return () => supabase.removeChannel(ch)
  }, [recipient, load])

  async function toggleComplete(task) {
    const now = !task.is_completed
    setTasks((ts) => ts.map((t) => (t.id === task.id ? { ...t, is_completed: now } : t)))
    const { error } = await supabase
      .from('tasks')
      .update({
        is_completed: now,
        completed_at: now ? new Date().toISOString() : null,
        completed_by: now ? user.id : null,
      })
      .eq('id', task.id)
    if (error) {
      setTasks((ts) => ts.map((t) => (t.id === task.id ? { ...t, is_completed: !now } : t)))
      toast.error(humanizeError(error))
    }
  }

  async function assignTo(task, userId) {
    setAssigning(null)
    const member = team.find((m) => m.user_id === userId)
    setTasks((ts) =>
      ts.map((t) =>
        t.id === task.id
          ? { ...t, assigned_to: userId, assignee: member ? { full_name: member.name } : null }
          : t
      )
    )
    const { error } = await supabase.from('tasks').update({ assigned_to: userId }).eq('id', task.id)
    if (error) { toast.error(humanizeError(error)); load() }
  }

  if (!recipient || loading) return <LoadingSpinner label="Loading tasks…" />

  const shown = mineOnly ? tasks.filter((t) => t.assigned_to === user.id) : tasks
  const open = shown.filter((t) => !t.is_completed)
  const done = shown.filter((t) => t.is_completed)

  const weekEnd = new Date()
  weekEnd.setDate(weekEnd.getDate() + 7)
  const weekEndISO = weekEnd.toLocaleDateString('en-CA')

  const groups = [
    { key: 'overdue', label: 'Overdue', danger: true,
      items: open.filter((t) => t.due_date && t.due_date < today) },
    { key: 'today', label: 'Today',
      items: open.filter((t) => t.due_date === today) },
    { key: 'week', label: 'This week',
      items: open.filter((t) => t.due_date && t.due_date > today && t.due_date <= weekEndISO) },
    { key: 'later', label: 'Later',
      items: open.filter((t) => !t.due_date || t.due_date > weekEndISO) },
  ].filter((g) => g.items.length > 0)

  const TaskRow = ({ t }) => {
    const overdue = !t.is_completed && t.due_date && t.due_date < today
    const picking = assigning === t.id
    return (
      <div className="card" style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <button
          onClick={() => toggleComplete(t)}
          aria-label={t.is_completed ? 'Mark incomplete' : 'Mark complete'}
          style={{
            marginTop: 2, width: 24, height: 24, borderRadius: 7, flexShrink: 0,
            border: t.is_completed ? 'none' : '2px solid var(--border)',
            background: t.is_completed ? 'var(--green)' : 'transparent',
            color: '#fff', fontSize: 15,
          }}
        >
          {t.is_completed ? '✓' : ''}
        </button>

        <div className="grow">
          <div
            style={{
              fontWeight: 600,
              textDecoration: t.is_completed ? 'line-through' : 'none',
              color: t.is_completed ? 'var(--muted)' : 'var(--text)',
            }}
          >
            {t.title}
          </div>
          {t.description && (
            <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>{t.description}</div>
          )}

          <div className="row wrap" style={{ gap: 6, marginTop: 6 }}>
            {t.due_date && (
              <span className={`badge${overdue ? ' badge-danger' : ''}`}>
                {overdue ? 'Overdue ' : 'Due '}{fmtDate(t.due_date)}
              </span>
            )}
            {t.assignee?.full_name ? (
              <span className="badge">
                <span
                  aria-hidden
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 16, height: 16, borderRadius: 999, marginRight: 2,
                    background: 'var(--pa)', color: '#fff', fontSize: 9, fontWeight: 700,
                  }}
                >
                  {initialOf(t.assignee.full_name)}
                </span>
                {t.assignee.full_name}
              </span>
            ) : t.is_completed ? null : canManage ? (
              <button
                className="badge badge-amber"
                onClick={() => setAssigning(picking ? null : t.id)}
                style={{ border: 'none', minHeight: 28 }}
              >
                Unassigned ▾
              </button>
            ) : (
              <span className="badge badge-amber">Unassigned</span>
            )}
            {t.is_recurring && <span className="badge">↻ Recurring</span>}
          </div>

          {picking && (
            <div className="card" style={{ marginTop: 8, padding: 8 }}>
              <div className="hint" style={{ marginBottom: 6 }}>Assign to</div>
              <div className="stack" style={{ gap: 6 }}>
                {team.length === 0 && (
                  <div className="muted" style={{ fontSize: 13 }}>No team members yet.</div>
                )}
                {team.map((m) => (
                  <button
                    key={m.user_id}
                    className="chip"
                    style={{ justifyContent: 'flex-start' }}
                    onClick={() => assignTo(t, m.user_id)}
                  >
                    {m.name}{m.user_id === user.id ? ' (you)' : ''}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {canManage && !t.is_completed && (
          <button
            onClick={() => setForm({ task: t })}
            style={{ background: 'none', border: 'none', color: 'var(--pa)', fontSize: 13, flexShrink: 0 }}
          >
            Edit
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="stack">
      <div className="spread">
        <h1 className="title">Tasks</h1>
        {canManage && <button className="btn btn-sm" onClick={() => setForm({})}>+ Add</button>}
      </div>

      {/* "My tasks only" is a filter, not a separate view */}
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button
          className={`chip${mineOnly ? ' chip-on' : ''}`}
          aria-pressed={mineOnly}
          onClick={() => setMineOnly((v) => !v)}
          style={{ fontSize: 13, minHeight: 38 }}
        >
          {mineOnly ? '✓ ' : ''}My tasks only
        </button>
      </div>

      {shown.length === 0 ? (
        <EmptyState
          icon="✅"
          title={mineOnly ? 'Nothing assigned to you' : 'No tasks yet'}
          message={canManage ? 'Add a task and assign it to the care team.' : 'Tasks assigned to you will appear here.'}
          action={canManage && <button className="btn" onClick={() => setForm({})}>+ Add task</button>}
        />
      ) : (
        <>
          {groups.map((g) => (
            <div key={g.key}>
              <div
                className="section-title"
                style={{ marginBottom: 8, color: g.danger ? 'var(--danger)' : undefined }}
              >
                {g.label} ({g.items.length})
              </div>
              <div className="stack" style={{ gap: 10 }}>
                {g.items.map((t) => <TaskRow key={t.id} t={t} />)}
              </div>
            </div>
          ))}

          {done.length > 0 && (
            <div>
              <button
                onClick={() => setShowDone((v) => !v)}
                className="section-title"
                style={{
                  background: 'none', border: 'none', padding: 0, marginBottom: 8,
                  display: 'flex', alignItems: 'center', gap: 6, minHeight: 44,
                }}
              >
                <span aria-hidden>{showDone ? '▾' : '▸'}</span>
                Completed ({done.length})
              </button>
              {showDone && (
                <div className="stack" style={{ gap: 10 }}>
                  {done.slice(0, 5).map((t) => <TaskRow key={t.id} t={t} />)}
                  {done.length > 5 && (
                    <div className="muted center" style={{ fontSize: 13 }}>
                      Showing the 5 most recent of {done.length}.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {form && (
        <TaskForm
          recipientId={recipient.id}
          task={form.task}
          team={team}
          onCancel={() => setForm(null)}
          onDone={() => { setForm(null); load() }}
        />
      )}

      <div style={{ height: 8 }} />
    </div>
  )
}
